/**
 * A CONVERSA COM O SERVIDOR — um lugar só, para as telas e para a fila.
 *
 * Até 25/09/2026 eram dois: a fila (`sincronizar`) ganhou limite de espera e a
 * regra do erro passageiro, e as telas (`chamar`) ficaram sem os dois. Com
 * sinal fraco, uma tela podia ficar em «Carregando…» para sempre, e um 502 da
 * Cloudflare virava «não deu certo». A mesma causa, consertada num caminho só.
 * Agora os dois perguntam aqui, e a resposta vem classificada.
 *
 * ── OS CINCO DESFECHOS ────────────────────────────────────────────────────
 *
 *   ok           o servidor aceitou.
 *   sem_conexao  NÃO HÁ CONEXÃO: o pedido nem saiu do aparelho.
 *   falhou       A CONEXÃO FALHOU: o pedido saiu e a resposta não voltou
 *                direito — o tempo acabou, ou veio uma página de erro sem
 *                explicação (5xx da Cloudflare), ou 408, ou 429. Passa sozinho.
 *   sem_acesso   o crachá deste aparelho caiu (401). É sessão, não o pedido.
 *   recusa       o NOSSO servidor respondeu, e explicou por que não. Inclusive
 *                o 500 com motivo — regra do banco violada, migração faltando —,
 *                que não passa sozinho e precisa de alguém olhando.
 *
 * A diferença entre `falhou` e `recusa` num 500 está na resposta: o nosso
 * servidor sempre explica (`{ erro }`); a página da Cloudflare, não.
 */

/**
 * QUANTO SE ESPERA POR UMA RESPOSTA antes de desistir desta vez.
 *
 * Sem limite, um pedido com sinal fraco ficava pendurado por minutos. Vinte
 * segundos cobrem com folga um banco que estava dormindo (a Neon acorda em
 * poucos segundos).
 *
 * NA FILA, DESISTIR NÃO PERDE NADA, e reenviar não duplica: o pedido pode até
 * ter chegado ao banco, mas o identificador nasceu no aparelho, e o banco
 * responde «já estava aqui» na próxima tentativa.
 *
 * FORA DA FILA ISSO NÃO VALE. Entrar com o código gasta o código no servidor;
 * se a resposta se perde, tentar de novo dá «código inválido». Quem chama um
 * pedido desses diz isso na tela — ver `Entrada.tsx`.
 */
export const ESPERA_MAXIMA_MS = 20_000

export type Desfecho =
  | { tipo: 'ok'; dados: Record<string, unknown> }
  | { tipo: 'sem_conexao' }
  /**
   * `respondeu`: o servidor chegou a responder (com erro sem explicação). Só
   * essa falha diz alguma coisa do REGISTRO; o tempo esgotado é quase sempre
   * o sinal, e não pode pesar contra ele.
   */
  | { tipo: 'falhou'; motivo: string; respondeu: boolean }
  | { tipo: 'sem_acesso'; erro: string }
  | { tipo: 'recusa'; status: number; erro: string }

/** Um pedido ao servidor: GET sem corpo, POST com. Nunca lança — classifica. */
export async function pedir(
  caminho: string,
  corpo?: unknown,
  esperaMaximaMs: number = ESPERA_MAXIMA_MS,
): Promise<Desfecho> {
  // Navegador sem AbortController (muito velho) fica sem limite de espera, mas
  // continua conversando — em vez de cair aqui.
  const relogio = typeof AbortController === 'function' ? new AbortController() : null
  const prazo = relogio ? setTimeout(() => relogio.abort(), esperaMaximaMs) : undefined
  // FOI O NOSSO RELÓGIO QUE CANCELOU? Pergunta-se ao relógio, e não ao nome do
  // erro, que cada navegador escreve de um jeito.
  const tempoAcabou = (): boolean => relogio?.signal.aborted === true
  const naoRespondeu: Desfecho = {
    tipo: 'falhou',
    motivo: 'o servidor não respondeu a tempo',
    respondeu: false,
  }

  try {
    let resposta: Response
    try {
      resposta = await fetch(`/api/${caminho}`, {
        credentials: 'same-origin',
        ...(corpo !== undefined
          ? {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify(corpo),
            }
          : {}),
        ...(relogio ? { signal: relogio.signal } : {}),
      })
    } catch {
      return tempoAcabou() ? naoRespondeu : { tipo: 'sem_conexao' }
    }

    const dados = (await resposta.json().catch(() => null)) as Record<string, unknown> | null
    // O RELÓGIO ESTOUROU NO MEIO DA LEITURA. Resposta pela metade não é
    // veredito: um 400 cuja explicação não chegou seria gravado como recusa
    // sem motivo, e ninguém saberia se apaga ou corrige.
    if (tempoAcabou()) return naoRespondeu

    const erro = dados !== null && typeof dados['erro'] === 'string' ? dados['erro'] : null
    if (resposta.ok) return { tipo: 'ok', dados: dados ?? {} }
    if (resposta.status === 401) return { tipo: 'sem_acesso', erro: erro ?? 'entre de novo' }
    if (
      resposta.status === 408 ||
      resposta.status === 429 ||
      (resposta.status >= 500 && erro === null)
    ) {
      return {
        tipo: 'falhou',
        motivo: `o servidor devolveu o erro ${resposta.status}, sem explicação`,
        respondeu: true,
      }
    }
    return { tipo: 'recusa', status: resposta.status, erro: erro ?? 'o servidor recusou' }
  } finally {
    if (prazo !== undefined) clearTimeout(prazo)
  }
}
